import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Send, Heart, Sparkles, MessageCircle, HelpCircle } from 'lucide-react';
import StarAnimation from '../components/StarAnimation';

interface BirthData {
  name: string;
  day: number;
  month: number;
  year: number;
  hour: number;
  min: number;
  lat: number;
  lon: number;
  tzone: number;
  place_of_birth: string;
}

interface MatchmakingReport {
  analysis: string;
  chartData: {
    charts: {
      match_ashtakoot_points: {
        total: {
          total_points: number;
          received_points: number;
        };
      };
      match_simple_report: {
        ashtakoota: {
          status: boolean;
          received_points: number;
        };
        manglik: {
          status: boolean;
          male_percentage: number;
          female_percentage: number;
        };
        rajju_dosha: {
          status: boolean;
        };
        vedha_dosha: {
          status: boolean;
        };
        conclusion: {
          match_report: string;
        };
      };
    };
  };
  matchType: string;
  focusAreas: string[];
  confidence: number;
  sources: string[];
  male: string;
  female: string;
  importedCharts: string[];
  timestamp: string;
}

interface MatchmakingChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isLoading?: boolean;
  isExpanded?: boolean;
}

const MatchmakingChat: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { matchmakingReport, maleData, femaleData } = location.state || {};
  
  const [messages, setMessages] = useState<MatchmakingChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Add welcome message on first load
  useEffect(() => {
    if (messages.length === 0 && matchmakingReport && maleData && femaleData) {
      const compatibilityScore = Math.round(
        (matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.received_points || 0) / 
        (matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.total_points || 36) * 100
      );
      
      const welcomeMessage: MatchmakingChatMessage = {
        id: 'welcome',
        type: 'system',
        content: `🌟 Welcome to your Compatibility Chat!

I have analyzed the match between ${maleData.name} and ${femaleData.name}. Here's what I found:

**Overall Compatibility Score:** ${compatibilityScore}%

**Key Findings:**
• Ashtakoota Points: ${matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.received_points || 0}/${matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.total_points || 36}
• Manglik Status: ${matchmakingReport?.chartData?.charts?.match_simple_report?.manglik?.status ? 'Present' : 'Not Present'}
• Rajju Dosha: ${matchmakingReport?.chartData?.charts?.match_simple_report?.rajju_dosha?.status ? 'Present' : 'Not Present'}
• Vedha Dosha: ${matchmakingReport?.chartData?.charts?.match_simple_report?.vedha_dosha?.status ? 'Present' : 'Not Present'}

**Ask me about:**
• "What does our compatibility score mean?"
• "How can we improve our relationship?"
• "What are the doshas and how do they affect us?"
• "Tell me about our planetary compatibility"
• "What are the strengths and challenges in our match?"

I have access to your complete matchmaking analysis and can provide detailed insights!`,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [matchmakingReport, maleData, femaleData]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    if (!matchmakingReport || !maleData || !femaleData) {
      toast.error('Matchmaking data not available. Please run a compatibility analysis first.');
      return;
    }

    const userMessage: MatchmakingChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Prepare the context with matchmaking data
      const compatibilityScore = Math.round(
        (matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.received_points || 0) /
          (matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.total_points || 36) *
          100
      );

      // Normalize partner birth data to match backend schema
      const normalizeBirth = (d: BirthData) => ({
        name: d.name,
        day: d.day,
        month: d.month,
        year: d.year,
        hour: d.hour,
        minute: d.min,
        latitude: d.lat,
        longitude: d.lon,
        timezone: d.tzone,
        placeOfBirth: d.place_of_birth
      });

      const response = await fetch('https://rraasibackend--jain-temple-trust.asia-east1.hosted.app/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: inputMessage,
          userContext: {
            userId: (matchmakingReport as any)?.userId || 'matchmaking_user',
            isMatchmakingChat: true,
            maleData: normalizeBirth(maleData),
            femaleData: normalizeBirth(femaleData),
            matchmakingCharts: matchmakingReport?.chartData,
            compatibilityScore: compatibilityScore,
            matchType: matchmakingReport?.matchType
          }
        })
      });

      const result = await response.json();

      if (result.success) {
        const assistantMessage: MatchmakingChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: result.response,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        const errorMessage: MatchmakingChatMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: result.error || 'I apologize, but I couldn\'t process your request. Please try again.',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error processing chat message:', error);
      
      const errorMessage: MatchmakingChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const extractSummaryAndDetails = (content: string) => {
    // Look for summary section (usually at the end)
    const summaryMatch = content.match(/(?:In summary|In conclusion|Overall|Summary|Key Points|Bottom Line)[^]*?$/i);
    
    if (summaryMatch) {
      const summary = summaryMatch[0].trim();
      const details = content.replace(summary, '').trim();
      return { summary, details };
    }
    
    // If no clear summary found, take first 2-3 paragraphs as summary
    const paragraphs = content.split(/\n\n/);
    if (paragraphs.length > 4) {
      const summary = paragraphs.slice(0, 2).join('\n\n');
      const details = paragraphs.slice(2).join('\n\n');
      return { summary, details };
    }
    
    // If content is short, show everything
    return { summary: content, details: '' };
  };

  const formatMatchmakingResponse = (content: string, isExpanded: boolean = false) => {
    const { summary, details } = extractSummaryAndDetails(content);
    
    return (
      <div className="space-y-4">
        {/* Summary Section */}
        <div className="bg-gradient-to-r from-pink-500/15 to-red-500/15 rounded-xl p-5 border border-pink-500/30 shadow-lg">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-pink-500/20 rounded-full flex items-center justify-center mr-3">
              <Heart className="w-4 h-4 text-pink-300" />
            </div>
            <h4 className="text-base font-bold text-pink-200">Compatibility Insights</h4>
          </div>
          <div className="text-sm leading-relaxed text-slate-200" 
               dangerouslySetInnerHTML={{ __html: summary.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-pink-200">$1</strong>') }} />
        </div>
        
        {/* Detailed Analysis (if expanded) */}
        {isExpanded && details && (
          <div className="mt-4">
            <div className="flex items-center mb-4">
              <div className="w-1 h-1 bg-pink-400 rounded-full mr-2"></div>
              <h4 className="text-sm font-semibold text-pink-200">Detailed Analysis</h4>
            </div>
            {formatDetailedContent(details)}
          </div>
        )}
      </div>
    );
  };

  const formatDetailedContent = (content: string) => {
    // Split content into sections by ### headers
    const sections = content.split(/(###\s+[^\n]+)/g);
    
    return sections.map((section, index) => {
      if (section.startsWith('###')) {
        // This is a header
        const headerText = section.replace(/^###\s+/, '').replace(/\*\*/g, '');
        return (
          <div key={index} className="mb-6">
            <h3 className="text-xl font-bold text-pink-200 mb-4 flex items-center bg-gradient-to-r from-pink-500/20 to-red-500/20 rounded-lg px-4 py-3 border border-pink-500/30">
              <Sparkles className="w-5 h-5 mr-3 text-pink-300" />
              {headerText}
            </h3>
          </div>
        );
      } else if (section.trim()) {
        // This is content
        const paragraphs = section.split(/\n\n/);
        return (
          <div key={index} className="space-y-4">
            {paragraphs.map((paragraph, pIndex) => {
              if (paragraph.trim()) {
                // Check if it's a sub-header (starts with ** and ends with **)
                if (paragraph.match(/^\*\*[^*]+\*\*:/)) {
                  const subHeaderText = paragraph.replace(/^\*\*([^*]+)\*\*:\s*/, '$1:');
                  return (
                    <div key={pIndex} className="mb-4">
                      <h4 className="text-base font-semibold text-pink-200 mb-3 flex items-center bg-pink-500/10 rounded-lg px-3 py-2 border-l-4 border-pink-400">
                        <div className="w-2 h-2 bg-pink-400 rounded-full mr-3"></div>
                        {subHeaderText}
                      </h4>
                    </div>
                  );
                } else {
                  // Handle bullet points and other formatting
                  const formattedParagraph = formatParagraph(paragraph);
                  return (
                    <div key={pIndex} className="mb-4">
                      {formattedParagraph}
                    </div>
                  );
                }
              }
              return null;
            })}
          </div>
        );
      }
      return null;
    });
  };

  const formatParagraph = (paragraph: string) => {
    // Handle bullet points
    if (paragraph.startsWith('- **')) {
      const parts = paragraph.split(':**');
      if (parts.length === 2) {
        const title = parts[0].replace('- **', '');
        const content = parts[1];
        return (
          <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/50">
            <h5 className="text-sm font-semibold text-pink-200 mb-2 flex items-center">
              <div className="w-1.5 h-1.5 bg-pink-400 rounded-full mr-2"></div>
              {title}:
            </h5>
            <p className="text-sm leading-relaxed text-slate-200">
              {content}
            </p>
          </div>
        );
      }
    }
    
    // Handle regular paragraphs with bold text
    const formattedText = paragraph.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-pink-200">$1</strong>');
    
    return (
      <p className="text-sm leading-relaxed text-slate-200" 
         dangerouslySetInnerHTML={{ __html: formattedText }} />
    );
  };

  const getMessageStyle = (message: MatchmakingChatMessage) => {
    switch (message.type) {
      case 'user':
        return 'bg-gradient-to-r from-pink-500 to-red-500 text-white ml-auto';
      case 'assistant':
        return 'bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm text-slate-100 border border-slate-700/50 shadow-xl';
      case 'system':
        return 'bg-pink-500/20 backdrop-blur-sm text-pink-100 border border-pink-500/30';
      default:
        return 'bg-white/10 backdrop-blur-sm text-pink-100';
    }
  };

  if (!matchmakingReport || !maleData || !femaleData) {
    return (
      <div className="flex flex-col h-screen bg-gradient-to-br from-pink-900 via-red-900 to-purple-900 relative overflow-hidden">
        <StarAnimation starCount={15} />
        <div className="relative z-10 flex flex-col h-full">
          <div className="bg-pink-800/80 backdrop-blur-sm text-white p-4 shadow-lg border-b border-white/20">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/matchmaking')}
                className="p-2 hover:bg-pink-700 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center">
                  <Heart className="w-5 h-5 mr-2" />
                  Compatibility Chat
                </h1>
                <p className="text-sm text-pink-200">Ask about your compatibility analysis</p>
              </div>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Heart className="w-16 h-16 text-pink-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">No Matchmaking Data</h2>
              <p className="text-pink-200 mb-4">Please run a compatibility analysis first to access the chat.</p>
              <button
                onClick={() => navigate('/matchmaking')}
                className="bg-gradient-to-r from-pink-500 to-red-500 text-white px-6 py-3 rounded-xl hover:from-pink-600 hover:to-red-600 transition-all"
              >
                Go to Matchmaking
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-pink-900 via-red-900 to-purple-900 relative overflow-hidden">
      {/* Star Animation */}
      <StarAnimation starCount={15} />
      
      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <div className="bg-pink-800/80 backdrop-blur-sm text-white p-4 shadow-lg border-b border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/matchmaking')}
                className="p-2 hover:bg-pink-700 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center">
                  <Heart className="w-5 h-5 mr-2" />
                  Compatibility Chat
                </h1>
                <p className="text-sm text-pink-200">Ask about {maleData.name} & {femaleData.name}'s compatibility</p>
              </div>
            </div>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="p-2 hover:bg-pink-700 rounded-full transition-colors"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Help Panel */}
        {showHelp && (
          <div className="bg-pink-500/20 backdrop-blur-sm p-4 border-b border-pink-500/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-pink-100 font-semibold flex items-center">
                <HelpCircle className="w-4 h-4 mr-2" />
                How to Use
              </h3>
              <button
                onClick={() => setShowHelp(false)}
                className="text-pink-200 hover:text-white"
              >
                ×
              </button>
            </div>
            <div className="text-pink-100 text-sm space-y-2">
              <p><strong>Compatibility Questions:</strong> Ask about your compatibility score, doshas, and relationship insights</p>
              <p><strong>Examples:</strong></p>
              <ul className="ml-4 space-y-1">
                <li>• "What does our compatibility score mean?"</li>
                <li>• "How can we improve our relationship?"</li>
                <li>• "What are the doshas and how do they affect us?"</li>
                <li>• "Tell me about our planetary compatibility"</li>
              </ul>
              <p><strong>Compatibility Score:</strong> {Math.round((matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.received_points || 0) / (matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.total_points || 36) * 100)}%</p>
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map(message => {
            const isExpanded = expandedMessages.has(message.id);
            const { details } = extractSummaryAndDetails(message.content);
            const hasDetails = details && details.trim().length > 0;
            
            return (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] rounded-2xl p-4 ${getMessageStyle(message)}`}>
                  {message.type === 'assistant' ? (
                    <div className="space-y-4">
                      <div className="flex items-center mb-3 text-xs text-pink-300">
                        <Heart className="w-3 h-3 mr-1" />
                        Compatibility Analysis
                      </div>
                      {formatMatchmakingResponse(message.content, isExpanded)}
                      
                      {/* Show More/Less Button */}
                      {hasDetails && (
                        <button
                          onClick={() => toggleMessageExpansion(message.id)}
                          className="mt-4 text-xs text-pink-300 hover:text-pink-200 flex items-center transition-colors"
                        >
                          {isExpanded ? (
                            <>
                              <span>Show Less</span>
                              <svg className="w-3 h-3 ml-1 transform rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </>
                          ) : (
                            <>
                              <span>Show Detailed Analysis</span>
                              <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap leading-relaxed">{message.content}</div>
                  )}
                  <div className="text-xs opacity-75 mt-4 flex items-center justify-between border-t border-white/10 pt-2">
                    <span>{message.timestamp.toLocaleTimeString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white/10 backdrop-blur-sm text-pink-100 rounded-2xl p-4">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  <span className="text-sm ml-2">Analyzing compatibility...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="bg-pink-800/50 backdrop-blur-sm p-4 border-t border-white/20">
          <div className="flex items-end space-x-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask about your compatibility, doshas, or relationship insights..."
                className="w-full bg-white/10 backdrop-blur-sm text-white placeholder-pink-300 rounded-2xl p-4 resize-none focus:outline-none focus:ring-2 focus:ring-pink-500 transition-all"
                rows={1}
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
            </div>
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="bg-gradient-to-r from-pink-500 to-red-500 text-white p-4 rounded-2xl hover:from-pink-600 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
              style={{ minWidth: '48px', minHeight: '48px' }}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          
          {/* Quick Tips */}
          <div className="mt-3 text-xs text-pink-300 flex items-center justify-between">
            <span>💕 Ask about compatibility, doshas, and relationship insights</span>
            <span>Compatibility Score: {Math.round((matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.received_points || 0) / (matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.total_points || 36) * 100)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchmakingChat; 